import React from "react";
import { IoLocationOutline } from "react-icons/io5";
import { IoBusiness } from "react-icons/io5";
import { IoPhonePortraitOutline } from "react-icons/io5";
import { IoKeyOutline } from "react-icons/io5";
import { IoIdCardOutline } from "react-icons/io5";
import { SHARED_RECORD_TYPES, SHARING_LABELS, SHARING_PILL } from "utils/sharing";
import OfficePortalAccess from "./OfficePortalAccess";

export default function OfficeView({ office }) {
  if (!office) return null;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <div className="bg-white">
      {/* Header */}
      <div className="bg-lightgray p-6 rounded-lg mb-6">
        {office.image && (
          <div className="mb-4">
            <img
              src={office.image}
              alt={office.name}
              className="w-20 h-20 object-cover rounded-lg border border-mediumgray"
            />
          </div>
        )}
        <div className="text-base font-semibold text-black mb-2">
          {office.name}
        </div>
        <div className="flex items-start gap-2">
          <IoLocationOutline className="text-darkergray flex-shrink-0 mt-0.5 text-base" />
          <p className="text-darkergray">
            {office.address && office.suburb
              ? `${office.address}, ${office.suburb}`
              : office.address || office.suburb || "No address specified"}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="space-y-6">
        {/* Basic Information */}
        <div>
          <div className="font-medium text-black mb-3">Basic Information</div>
          <div className="bg-lightgray rounded-lg p-4 space-y-3">
            <div className="flex gap-2 justify-between">
              <span className="text-darkgray">Office Name:</span>
              <span className="text-black font-medium">{office.name}</span>
            </div>
            {office.officeId && (
              <div className="flex gap-2 justify-between">
                <span className="text-darkgray">Office ID:</span>
                <span className="text-black">{office.officeId}</span>
              </div>
            )}
            {office.domainId && (
              <div className="flex gap-2 justify-between">
                <span className="text-darkgray">Domain ID:</span>
                <span className="text-black">{office.domainId}</span>
              </div>
            )}
            {office.reaId && (
              <div className="flex gap-2 justify-between">
                <span className="text-darkgray">REA ID:</span>
                <span className="text-black">{office.reaId}</span>
              </div>
            )}
          </div>
        </div>

        {/* Printed Contact Details */}
        <div>
          <div className="font-medium text-black mb-3">
            Printed Contact Details
          </div>
          <div className="bg-lightgray rounded-lg p-4 space-y-3">
            <div className="flex gap-2 justify-between">
              <span className="text-darkgray">Phone:</span>
              <span className="text-black">{office.phone || "Not set"}</span>
            </div>
            <div className="flex gap-2 justify-between">
              <span className="text-darkgray">Email:</span>
              <span className="text-black break-all">
                {office.email || "Not set"}
              </span>
            </div>
            <p className="text-xs text-darkgray pt-1">
              Printed on this office's letterheads, brochures and signboards.
              Left blank, the artwork prints blank.
            </p>
          </div>
        </div>

        {/* Location Information */}
        <div>
          <div className="font-medium text-black mb-3">
            Location Information
          </div>
          <div className="bg-lightgray rounded-lg p-4 space-y-3">
            {office.address && (
              <div className="flex gap-2 justify-between">
                <span className="text-darkgray">Address:</span>
                <span className="text-black">{office.address}</span>
              </div>
            )}
            {office.suburb && (
              <div className="flex gap-2 justify-between">
                <span className="text-darkgray">Suburb:</span>
                <span>{office.suburb}</span>
              </div>
            )}
            <div className="flex gap-2 justify-between items-center">
              <span className="text-darkgray">Jurisdiction:</span>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  office.jurisdiction
                    ? "bg-blue-100 text-blue-800"
                    : "bg-mediumgray text-darkergray"
                }`}
              >
                {office.jurisdiction || "Not set"}
              </span>
            </div>
          </div>
        </div>

        {/* Share Across Teams */}
        <div>
          <div className="font-medium text-black mb-3">Share Across Teams</div>
          <div className="bg-lightgray rounded-lg p-4 space-y-3">
            {SHARED_RECORD_TYPES.map((recordType) => (
              <div
                key={recordType.key}
                className="flex gap-2 justify-between items-center"
              >
                <span className="text-darkgray">{recordType.name}:</span>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    SHARING_PILL[office.sharing?.[recordType.key]] ||
                    SHARING_PILL.inherit
                  }`}
                >
                  {SHARING_LABELS[office.sharing?.[recordType.key]] ||
                    SHARING_LABELS.inherit}
                </span>
              </div>
            ))}
            <p className="text-xs text-darkgray pt-1">
              A team is a main agent and their co-agents, and always shares
              within itself. These open a record type up to every team in the
              office.
            </p>
          </div>
        </div>

        {/* Campaign Agent Integration */}
        <div>
          <div className="font-medium text-black mb-3">
            Campaign Agent Integration
          </div>
          <div className="bg-lightgray rounded-lg p-4 border-l-4 border-purple-500">
            <div className="flex items-center gap-2 mb-3">
              <IoBusiness className="text-purple-600" />
              <span className="font-medium text-black">
                Campaign Agent Setup
              </span>
            </div>
            <div className="space-y-3">
              {office.caId && (
                <div className="flex gap-2 justify-between">
                  <span className="text-darkgray">CA ID (Outbound):</span>
                  <span>{office.caId}</span>
                </div>
              )}
              {office.caOfficeId && (
                <div className="flex gap-2 justify-between">
                  <span className="text-darkgray">CA Office ID (Inbound):</span>
                  <span>{office.caOfficeId}</span>
                </div>
              )}
            </div>
            <div className="mt-4 text-xs text-darkgray space-y-1">
              <p>• CA ID: Identifier sent to Campaign Agent</p>
              <p>• CA Office ID: Identifier Campaign Agent sends back</p>
            </div>
          </div>
        </div>

        {/* SMS Integration */}
        <div>
          <div className="font-medium text-black mb-3">SMS Integration</div>
          <div className="bg-lightgray rounded-lg p-4 border-l-4 border-blue-500">
            <div className="flex items-center gap-2 mb-3">
              <IoPhonePortraitOutline className="text-blue-600" />
              <span className="font-medium text-black">SMS Configuration</span>
            </div>
            <div className="space-y-3">
              {office.smsUser && (
                <div className="flex gap-2 justify-between">
                  <span className="text-darkgray">SMS Username:</span>
                  <span>{office.smsUser}</span>
                </div>
              )}
              {office.smsPass && (
                <div className="flex gap-2 justify-between">
                  <span className="text-darkgray">SMS Password:</span>
                  <span>{office.smsPass}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* AML Partners Integration */}
        <div>
          <div className="font-medium text-black mb-3">
            AML Partners Integration
          </div>
          <div className="bg-lightgray rounded-lg p-4 border-l-4 border-green-500">
            <div className="flex items-center gap-2 mb-3">
              <IoKeyOutline className="text-green-600" />
              <span className="font-medium text-black">AML Configuration</span>
            </div>
            <div className="space-y-3">
              {office.amlUsername && (
                <div className="flex gap-2 justify-between">
                  <span className="text-darkgray">AML Username:</span>
                  <span>{office.amlUsername}</span>
                </div>
              )}
              {office.amlPassword && (
                <div className="flex gap-2 justify-between">
                  <span className="text-darkgray">AML Password:</span>
                  <span>{office.amlPassword}</span>
                </div>
              )}
              {!office.amlUsername && !office.amlPassword && (
                <p className="text-xs text-darkgray">
                  No AML credentials configured for this office.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Campaign Flow Integration */}
        <div>
          <div className="font-medium text-black mb-3">
            Campaign Flow Integration
          </div>
          <div className="bg-lightgray rounded-lg p-4 border-l-4 border-orange-500">
            <div className="flex items-center gap-2 mb-3">
              <IoIdCardOutline className="text-orange-600" />
              <span className="font-medium text-black">
                Campaign Flow Configuration
              </span>
            </div>
            <div className="space-y-3">
              {office.cfClientId && (
                <div className="flex gap-2 justify-between">
                  <span className="text-darkgray">Client ID:</span>
                  <span className="text-black break-all">
                    {office.cfClientId}
                  </span>
                </div>
              )}
              {office.cfClientSecret && (
                <div className="flex gap-2 justify-between">
                  <span className="text-darkgray">Client Secret:</span>
                  <span className="text-black break-all">
                    {office.cfClientSecret}
                  </span>
                </div>
              )}
              {office.cfTenantId && (
                <div className="flex gap-2 justify-between">
                  <span className="text-darkgray">Tenant ID:</span>
                  <span className="text-black break-all">
                    {office.cfTenantId}
                  </span>
                </div>
              )}
              {office.cfWebhookSecret && (
                <div className="flex gap-2 justify-between">
                  <span className="text-darkgray">Webhook Secret:</span>
                  <span className="text-black break-all">
                    {office.cfWebhookSecret}
                  </span>
                </div>
              )}
              {/* Credentials are in place but nothing was registered — the
                  webhook is provisioned on save, so re-saving fixes it. */}
              {office.cfClientId &&
                office.cfClientSecret &&
                office.cfTenantId &&
                !office.cfWebhookSecret && (
                  <p className="text-xs text-orange-700">
                    Webhook not registered yet — save this office to register it
                    and capture the signing secret.
                  </p>
                )}
              {!office.cfClientId &&
                !office.cfClientSecret &&
                !office.cfTenantId &&
                !office.cfWebhookSecret && (
                  <p className="text-xs text-darkgray">
                    No Campaign Flow credentials configured for this office.
                  </p>
                )}
            </div>
          </div>
        </div>

        {/* Portal Access — live REA/Domain capability check */}
        <OfficePortalAccess office={office} />

        {/* System Information */}
        <div>
          <div className="font-medium text-black mb-3">System Information</div>
          <div className="bg-lightgray rounded-lg p-4 space-y-3">
            {office.createdAt && (
              <div className="flex justify-between">
                <span className="text-darkgray">Created:</span>
                <span className="text-black">
                  {formatDate(office.createdAt)}
                </span>
              </div>
            )}
            {office.updatedAt && (
              <div className="flex justify-between">
                <span className="text-darkgray">Last Updated:</span>
                <span className="text-black">
                  {formatDate(office.updatedAt)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
